import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  createInternalUser,
  deleteInternalUser,
  getAllManagedUser,
  getInternalUser,
  type InternalUserCreate,
  type InternalUserUpdate,
  type ManagedUserListParams,
  updateInternalUser,
} from "@/apis/admin/admin-user-apis";
import { queryKeys } from "./keys";

export function useManagedUsers(params: ManagedUserListParams) {
  return useQuery({
    queryKey: queryKeys.adminUsers.list(params),
    queryFn: () => getAllManagedUser(params),
    placeholderData: keepPreviousData,
  });
}

export function useInternalUser(email: string | null) {
  return useQuery({
    queryKey: queryKeys.adminUsers.detail(email ?? ""),
    // Query is disabled when email is null, so queryFn won't run with empty string
    queryFn: () => getInternalUser(email ?? ""),
    enabled: Boolean(email),
  });
}

export function useCreateInternalUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: InternalUserCreate) => createInternalUser(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.adminUsers.all,
        predicate: (query) => query.queryKey[1] === "list",
      });
    },
  });
}

export function useUpdateInternalUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ email, dto }: { email: string; dto: InternalUserUpdate }) =>
      updateInternalUser(email, dto),
    onSuccess: (_data, { email }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.adminUsers.detail(email),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.adminUsers.all,
        predicate: (query) => query.queryKey[1] === "list",
      });
    },
  });
}

export function useDeleteInternalUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (email: string) => deleteInternalUser(email),
    onSuccess: (_data, email) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.adminUsers.detail(email),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.adminUsers.all,
        predicate: (query) => query.queryKey[1] === "list",
      });
    },
  });
}
